

<template>
	<div class="illustration">
		<!-- Alles im CANVAS von Adobe Animate! -->
	</div>
</template>

<script>

import { EventBus } from '@/common/eventbus.js'
import { store } from '@/store'
import { mapState } from 'vuex'
import { Log } from '@/services/log.service'
import { Finder } from '@/services/finder.service'
import { Transition } from '@/services/transition.service'
import { KombiRepository } from '@/repositories/KombiRepository'
import { gsap, TweenMax, TimelineMax, Power2, Power3, Power4 } from 'gsap';

export default {
	store,
	props: ['selection', 'optionsVisible'],
	data: () => {
		return {
			initialized: false,
			stage_mc: false,
			main_mc: false,
			layers: {},
			buildOrder: ['flasche', 'verschluss', 'kopf', 'kappe', 'huelse', 'product'],
			selectionQueue: [],
			isBusy: false,
		}
	},
	
	computed: {
		...mapState('settings', ['presets']),
		...mapState('globals', ['animation']),
		stageHeight() {
			return this.animation.canvas.height * (1/this.animation.canvas.width * this.animation.width);
		},
		scaleFactor() {
			return this.animation.$div[0].offsetWidth < 600 ? 1.7 : 1;
		}
	},

	mounted() {
		this.prepareStageAssets();
	},
	
	watch: {
		selection( newVal, oldVal ) {
			this.selectionQueue = [];
			this.selectionQueue.push( Object.assign({}, newVal) );
			this.processNextInQueue();
		},
		optionsVisible( val ) {
			if (!this.main_mc) return;
			this.bitmapCacheTimer( 2 );
			TweenMax.to(this.stage_mc, 1.2, {
				delay: val ? 0.4 : 0.2,  
				x:val ? -350 : 0,
				ease: Power2.easeInOut,
			});
		}
	},

	methods: {

		/**
		 * 	CreateJS-Element als Bitmap cachen und nach
		 * 	bestimmter Zeit wieder freigeben.
		 * 	Performance-Tweek.
		 * 
		 */
		bitmapCacheTimer( dur, el ) {
			if (!el) el = this.main_mc;
			el.cache(-400, -400, 800, 800, 3);
			setTimeout(() => {
				el.uncache();
			}, dur * 1000);
		},

		/**
		 * 	Nächstes Element in Queue bearbeiten.
		 * 
		 */
		processNextInQueue() {
			if (this.isBusy) return;
			if (!this.selectionQueue.length) return;
			this.updateProductPreview();
		},

		/**
		 * 	Wartet, bis Bühne startklar ist.
		 * 	Erstellt Basis-Container für Illustrationen.
		 * 
		 */
		prepareStageAssets() {
			if (!this.animation.exportRoot || !this.animation.exportRoot.assets_mc) {
				this.$nextTick( prepareStageAssets );
				return;
			}
			this.animation.exportRoot.assets_mc.x = -5000;
			this.animation.exportRoot.assets_mc.visible = false;

			this.stage_mc = new createjs.Container();
			this.stage_mc.x = this.stage_mc.y = 0;
			
			this.main_mc = new createjs.Container();

			this.stage_mc.addChild( this.main_mc );
			this.animation.stage.addChild( this.stage_mc  );

			this.main_mc.x = this.animation.width / 2;
			this.main_mc.y = this.stageHeight;

			this.main_mc.scale = 2 * this.scaleFactor;
			this.initialized = true;
		},

		/**
		 * 	Wird bei Änderung / Auswahl der Komponenten durch den User
		 * 	aufgerufen. Zeigt/versteckt die Elemente in Adobe-Animate
		 * 
		 */
		updateProductPreview() {
			
			if (!this.initialized) return;
			
			var layerTransitions = {};

			var selection = this.selectionQueue.shift();

			Log.SUCCESS('updateProductPreview', '', selection);

			this.isBusy = true;
			if (!Object.keys(this.layers).length) {
				this.isBusy = false;
			}

			// ------------------------------------
			// Transition vorbereiten

			this.buildOrder.forEach((n) => {

				var sel = selection[n];
				
				if (!layerTransitions[n]) {
					layerTransitions[n] = {};
				}

				layerTransitions[n].from = this.layers[n] || false;

				if (!sel) {

					// Element soll entfernt werden
					layerTransitions[n].to = false;

				} else {

					// Element soll eingefügt werden
					var name = sel.lib || false;
					if (!name) return;

					if (!this.animation.lib[name]) {
						Log.ERROR('Illustration->updateProductPreview()', `${name} nicht in der Animate-Bibliothek gefunden`, this.animation.lib);
						return;
					}

					// Element aus Bibliothek (Animate) instanziieren
					var el = new this.animation.lib[name]();
					el.compData = sel;
					el.compName = n;

					this.main_mc.addChild( el );

					var frame = sel.frame ? sel.frame-1 : 0;
					if (sel.farbe && sel.farbe.frame) {
						frame = sel.farbe.frame - 1;
					}
					el.gotoAndStop(frame);

					if (sel.animated) {
						el.play();
					} else {
						this.bitmapCacheTimer(3, el);
					}
					
					var offsetY = sel.y ? sel.y : 0;

					el.y = offsetY;
					el.x = 0;

					layerTransitions[n].to = el;
					//test.cache(-200, -200, 400, 400, 3);
					//this.layers[n] = el;
				}
			});

			this.main_mc.sortChildren((objA, objB) => {
				var ta = objA.compData.zIndex;
				var tb = objB.compData.zIndex;
				return ta > tb ? 1 : -1;
			});


			Transition.init();

			// ---------------------------------------
			// Animationen des Gesamtcontainers?

			if (selection.viewport) {
				var props = {
					ease: Power4.easeInOut
				}
				if (selection.viewport.x) 		props.x = selection.viewport.x;
				if (selection.viewport.y) 		props.y = selection.viewport.y + this.stageHeight;
				if (selection.viewport.scale) 	props.scale = selection.viewport.scale * this.scaleFactor;
				TweenMax.to(this.main_mc, 1.5, props);
			}

			// ---------------------------------------
			// Animationen der Einzelteile ausführen

			for (var i in layerTransitions) {

				var trans = layerTransitions[i];

				var fromEl 	= trans.from;
				var toEl 	= trans.to;

				// Übergang von einem zu anderem Element
				if (fromEl && toEl) {

					if (fromEl.compData.lib == toEl.compData.lib && (toEl.compData.animated || fromEl.currentFrame == toEl.currentFrame) ) {

						// Das Element hat sich NICHT verändert. Nur Position anpassen, falls nötig.
						Transition.move( this.main_mc, fromEl, toEl, trans );

					} else {
						// Das Element wird ERSETZT. Swap-Animation.
						Transition.swap( this.main_mc, fromEl, toEl, trans );
					}
				}

				if (fromEl && !toEl) {
					// Das Element wurde entfernt
					Transition.disappear( this.main_mc, fromEl, trans );
					delete(this.layers[i]);
				}
				
				if (!fromEl && toEl) {
					// Das Element ist zum ersten Mal hier
					Transition.appear( this.main_mc, toEl, trans );
				}

				if (toEl) {
					this.layers[i] = toEl;
				}
			}

			this.animation.stage.update();
	

			Transition.onComplete( this.animationDone.bind(this) );

		},

		/**
		 * 	Callback nach Animations-Ende:
		 * 	Nächstes Element in Queue bearbeiten.
		 */
		animationDone() {
			this.isBusy = false;
			this.processNextInQueue();
		}

	}

}
</script>

<style scoped lang="scss">
	
</style>



// WEBPACK FOOTER //
// src/components/Illustration.vue